import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import type { OpgMuestraLocal, PosicionHpgValida } from './hpgDialogTypes';

interface HpgDialogOpgTabProps {
  muestras: OpgMuestraLocal[];
  posicionesHpgValidas: PosicionHpgValida[];
  onChange: (muestras: OpgMuestraLocal[]) => void;
  onEliminarTab: () => void;
}

export function HpgDialogOpgTab({
  muestras,
  posicionesHpgValidas,
  onChange,
  onEliminarTab,
}: HpgDialogOpgTabProps): JSX.Element {
  function update(index: number, patch: Partial<OpgMuestraLocal>): void {
    onChange(muestras.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  }

  function remove(index: number): void {
    onChange(muestras.filter((_, i) => i !== index));
  }

  function agregar(): void {
    onChange([
      ...muestras,
      { numero_muestra_hpg_referencia: null, identificacion: null, valor: null, observacion: null },
    ]);
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Tooltip title="Eliminar este OPG">
          <IconButton size="small" color="error" onClick={onEliminarTab}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }}>N°</TableCell>
              <TableCell sx={{ minWidth: 190 }}>Muestra HPG ref.</TableCell>
              <TableCell sx={{ width: 110 }}>Valor HPG</TableCell>
              <TableCell sx={{ minWidth: 130 }}>Identificación</TableCell>
              <TableCell sx={{ width: 120 }}>Valor OPG</TableCell>
              <TableCell sx={{ minWidth: 130 }}>Observación</TableCell>
              <TableCell sx={{ width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {muestras.map((m, index) => {
              const ref = m.numero_muestra_hpg_referencia;
              const hpgPos = ref !== null
                ? posicionesHpgValidas.find((p) => p.posicion === ref)
                : null;
              const isRefRota = ref !== null && hpgPos === undefined;

              return (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>

                  <TableCell>
                    <FormControl size="small" fullWidth error={isRefRota}>
                      <Select
                        value={ref !== null ? String(ref) : 'insuficiente'}
                        onChange={(e) => {
                          const v = e.target.value;
                          update(index, {
                            numero_muestra_hpg_referencia: v === 'insuficiente' ? null : Number(v),
                          });
                        }}
                        renderValue={(v) => {
                          if (v === 'insuficiente') return 'Insuficiente';
                          const pos = Number(v);
                          const found = posicionesHpgValidas.find((p) => p.posicion === pos);
                          if (!found) {
                            return (
                              <Typography variant="body2" color="error.main" component="span">
                                Muestra {pos} (eliminada)
                              </Typography>
                            );
                          }
                          return `Muestra ${pos} (${found.valor !== null ? found.valor : '—'} hpg)`;
                        }}
                      >
                        {posicionesHpgValidas.map((p) => (
                          <MenuItem key={p.posicion} value={String(p.posicion)}>
                            Muestra {p.posicion} ({p.valor !== null ? `${p.valor} hpg` : '—'})
                          </MenuItem>
                        ))}
                        {isRefRota && (
                          <MenuItem value={String(ref)} sx={{ color: 'error.main' }} disabled>
                            Muestra {ref} (eliminada)
                          </MenuItem>
                        )}
                        <MenuItem value="insuficiente">Insuficiente</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {ref === null || hpgPos == null
                        ? '—'
                        : (hpgPos.valor !== null ? hpgPos.valor : '—')}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={m.identificacion ?? ''}
                      onChange={(e) => update(index, { identificacion: e.target.value || null })}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      inputProps={{ min: 0, step: 1 }}
                      value={m.valor !== null ? String(m.valor) : ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        update(index, { valor: v === '' ? null : Number(v) });
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={m.observacion ?? ''}
                      onChange={(e) => update(index, { observacion: e.target.value || null })}
                    />
                  </TableCell>

                  <TableCell>
                    <IconButton size="small" onClick={() => remove(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}

            {muestras.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Sin muestras. Agregá al menos una.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <Button size="small" sx={{ mt: 1.5 }} onClick={agregar}>
        + Agregar muestra OPG
      </Button>
    </Box>
  );
}
